import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/components/forms.css';

export default function SubscriptionPlans() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedTier, setSelectedTier] = useState(null);
    const [processingSubscription, setProcessingSubscription] = useState(false);
    const [subscriptionMessage, setSubscriptionMessage] = useState('');
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchPricingPlans();
    }, []);

    const fetchPricingPlans = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
                { withCredentials: true }
            );
            
            // Filter to only show plans with tiers_mode = 1
            const tieredPlans = response.data.filter(plan => plan.tiers_mode === 1);
            
            if (tieredPlans.length > 0) {
                setPlans(tieredPlans);
            } else {
                setPlans([]);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
            setError('Failed to load subscription plans');
            setLoading(false);
        }
    };

    const handlePlanSelect = (plan, tier) => {
        setSelectedPlan(plan);
        setSelectedTier(tier);
    };

    const calculateSavings = (monthlyPrice, totalPrice, months) => {
        if (!monthlyPrice || !totalPrice || !months) return 0;
        const monthlyTotal = monthlyPrice * months;
        const savings = monthlyTotal - totalPrice;
        const savingsPercentage = (savings / monthlyTotal) * 100;
        return Math.round(savingsPercentage);
    };

    const handleSubscribe = async () => {
        if (!selectedTier) return;
        
        try {
            setProcessingSubscription(true);
            setSubscriptionMessage('Processing your subscription...');
            
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/create`,
                {
                    userId: user.id,
                    priceId: selectedTier.id // Use the tier ID
                },
                { withCredentials: true }
            );
            
            if (response.data.checkoutUrl) {
                // If we get a checkout URL, redirect to Stripe
                window.location.href = response.data.checkoutUrl;
            } else if (response.data.subscriptionId) {
                // If subscription created directly
                setSubscriptionMessage('Subscription created successfully!');
                setTimeout(() => {
                    navigate(`/users/${user.id}`);
                }, 2000);
            }
        } catch (error) {
            console.error('Error creating subscription:', error);
            setError(error.response?.data?.message || 'Failed to create subscription');
            setProcessingSubscription(false);
        }
    };

    if (loading) return <div className="loading">Loading plans...</div>;

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </Link>
                <h1>Abonnementsplaner</h1>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            {subscriptionMessage && <div className="success-message">{subscriptionMessage}</div>}
            
            <div className="subscription-container">
                <div className="subscription-intro">
                    <h2>Velg abonnementstype</h2>
                    <p>Velg mellom månedlig, halvårlig eller årlig abonnement. Lengre abonnement gir bedre pris.</p>
                </div>
                
                {plans.length === 0 ? (
                    <div className="no-plans-message">
                        Ingen abonnementsplaner er tilgjengelige akkurat nå.
                    </div>
                ) : (
                    <div className="plans-grid">
                        {plans.map(plan => {
                            // Only process plans with tiers
                            if (!plan.tiers || plan.tiers.length === 0) return null;
                            
                            // Find monthly, 6-month, and yearly tiers
                            const monthlyTier = plan.tiers.find(t => 
                                t.payment_interval === 'month' && t.interval_count === 1
                            );
                            
                            const sixMonthTier = plan.tiers.find(t => 
                                t.payment_interval === 'month' && t.interval_count === 6
                            );
                            
                            const yearlyTier = plan.tiers.find(t => 
                                t.payment_interval === 'year' && t.interval_count === 1
                            );
                            
                            // Require at least one tier to display
                            if (!monthlyTier && !sixMonthTier && !yearlyTier) return null;
                            
                            return (
                                <div key={plan.idpricing} className="plan-container">
                                    <div className="plan-header">
                                        <h2>{plan.name}</h2>
                                        <p>{plan.description}</p>
                                    </div>
                                    
                                    <div className="pricing-boxes">
                                        {monthlyTier && (
                                            <div 
                                                className={`pricing-box ${selectedTier?.id === monthlyTier.id ? 'selected' : ''}`}
                                                onClick={() => handlePlanSelect(plan, monthlyTier)}
                                            >
                                                <div className="pricing-period">
                                                    <h3>Månedlig</h3>
                                                </div>
                                                
                                                <div className="pricing-amount">
                                                    <span className="currency">NOK</span>
                                                    <span className="amount">{monthlyTier.amount}</span>
                                                    <span className="period">/ mnd</span>
                                                </div>
                                                
                                                <div className="monthly-equivalent"></div>
                                                
                                                <div className="pricing-features">
                                                    <ul>
                                                        <li><i className="fas fa-check"></i> Full tilgang til alle programmer</li>
                                                        <li><i className="fas fa-check"></i> Alle premium-aktiviteter</li>
                                                        <li><i className="fas fa-check"></i> Fremdriftsporing</li>
                                                    </ul>
                                                </div>
                                                
                                                <button 
                                                    className={`select-plan-button ${selectedTier?.id === monthlyTier.id ? 'selected' : ''}`}
                                                    onClick={() => handlePlanSelect(plan, monthlyTier)}
                                                >
                                                    {selectedTier?.id === monthlyTier.id ? 'Valgt' : 'Velg'}
                                                </button>
                                            </div>
                                        )}
                                        
                                        {sixMonthTier && (
                                            <div 
                                                className={`pricing-box ${selectedTier?.id === sixMonthTier.id ? 'selected' : ''}`}
                                                onClick={() => handlePlanSelect(plan, sixMonthTier)}
                                            >
                                                <div className="pricing-period">
                                                    <h3>6 Måneder</h3>
                                                </div>
                                                
                                                <div className="pricing-amount">
                                                    <span className="currency">NOK</span>
                                                    <span className="amount">{sixMonthTier.amount}</span>
                                                    <span className="period">/ 6 mnd</span>
                                                </div>
                                                
                                                <div className="monthly-equivalent">
                                                    <span>Tilsvarer {Math.round(sixMonthTier.amount / 6)} kr/mnd</span>
                                                </div>
                                                
                                                {monthlyTier && (
                                                    <div className="savings-badge">
                                                        <span>Spar {calculateSavings(
                                                            monthlyTier.amount,
                                                            sixMonthTier.amount,
                                                            6
                                                        )}%</span>
                                                    </div>
                                                )}
                                                
                                                <div className="pricing-features">
                                                    <ul>
                                                        <li><i className="fas fa-check"></i> Full tilgang til alle programmer</li>
                                                        <li><i className="fas fa-check"></i> Alle premium-aktiviteter</li>
                                                        <li><i className="fas fa-check"></i> Fremdriftsporing</li>
                                                        <li><i className="fas fa-check"></i> Ingen bindingstid</li>
                                                    </ul>
                                                </div>
                                                
                                                <button 
                                                    className={`select-plan-button ${selectedTier?.id === sixMonthTier.id ? 'selected' : ''}`}
                                                    onClick={() => handlePlanSelect(plan, sixMonthTier)}
                                                >
                                                    {selectedTier?.id === sixMonthTier.id ? 'Valgt' : 'Velg'}
                                                </button>
                                            </div>
                                        )}
                                        
                                        {yearlyTier && (
                                            <div 
                                                className={`pricing-box popular ${selectedTier?.id === yearlyTier.id ? 'selected' : ''}`}
                                                onClick={() => handlePlanSelect(plan, yearlyTier)}
                                            >
                                                <div className="popular-badge">
                                                    <span>Best Value</span>
                                                </div>
                                                
                                                <div className="pricing-period">
                                                    <h3>Årlig</h3>
                                                </div>
                                                
                                                <div className="pricing-amount">
                                                    <span className="currency">NOK</span>
                                                    <span className="amount">{yearlyTier.amount}</span>
                                                    <span className="period">/ år</span>
                                                </div>
                                                
                                                <div className="monthly-equivalent">
                                                    <span>Tilsvarer {Math.round(yearlyTier.amount / 12)} kr/mnd</span>
                                                </div>
                                                
                                                {monthlyTier && (
                                                    <div className="savings-badge">
                                                        <span>Spar {calculateSavings(
                                                            monthlyTier.amount,
                                                            yearlyTier.amount,
                                                            12
                                                        )}%</span>
                                                    </div>
                                                )}
                                                
                                                <div className="pricing-features">
                                                    <ul>
                                                        <li><i className="fas fa-check"></i> Full tilgang til alle programmer</li>
                                                        <li><i className="fas fa-check"></i> Alle premium-aktiviteter</li>
                                                        <li><i className="fas fa-check"></i> Fremdriftsporing</li>
                                                        <li><i className="fas fa-check"></i> Ingen bindingstid</li>
                                                    </ul>
                                                </div>
                                                
                                                <button 
                                                    className={`select-plan-button ${selectedTier?.id === yearlyTier.id ? 'selected' : ''}`}
                                                    onClick={() => handlePlanSelect(plan, yearlyTier)}
                                                >
                                                    {selectedTier?.id === yearlyTier.id ? 'Valgt' : 'Velg'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {selectedTier && (
                    <div className="checkout-section">
                        <button 
                            className="checkout-button"
                            onClick={handleSubscribe}
                            disabled={processingSubscription}
                        >
                            {processingSubscription ? (
                                <><i className="fas fa-spinner fa-spin"></i> Behandler...</>
                            ) : (
                                <>Gå til betaling</>
                            )}
                        </button>
                        <p className="checkout-note">
                            Du vil bli belastet NOK {selectedTier.amount} for 
                            {selectedTier.payment_interval === 'month' && selectedTier.interval_count === 1 && ' et månedlig abonnement'}
                            {selectedTier.payment_interval === 'month' && selectedTier.interval_count === 6 && ' et 6-måneders abonnement'}
                            {selectedTier.payment_interval === 'year' && ' et årlig abonnement'}
                        </p>
                    </div>
                )}
            </div>
            
            <style jsx>{`
                .subscription-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 30px 0;
                }
                
                .subscription-intro {
                    text-align: center;
                    margin-bottom: 40px;
                }
                
                .subscription-intro h2 {
                    font-size: 2rem;
                    margin-bottom: 15px;
                    color: #2d4a22;
                }
                
                .subscription-intro p {
                    font-size: 1.1rem;
                    color: #555;
                    max-width: 700px;
                    margin: 0 auto;
                }
                
                .plans-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 60px;
                }
                
                .plan-container {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                
                .plan-header {
                    text-align: center;
                    margin-bottom: 20px;
                }
                
                .plan-header h2 {
                    font-size: 1.8rem;
                    color: #2d4a22;
                    margin-bottom: 8px;
                }
                
                .plan-header p {
                    color: #555;
                    font-size: 1.1rem;
                }
                
                .pricing-boxes {
                    display: flex;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 30px;
                    margin-bottom: 40px;
                }
                
                .pricing-box {
                    flex: 1;
                    min-width: 280px;
                    max-width: 350px;
                    background-color: white;
                    border-radius: 12px;
                    padding: 30px;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                    position: relative;
                    border: 2px solid transparent;
                    cursor: pointer;
                }
                
                .pricing-box:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
                }
                
                .pricing-box.selected {
                    border-color: #1d3618;
                }
                
                .pricing-box.popular {
                    border-color: #1d3618;
                    transform: scale(1.05);
                }
                
                .pricing-box.popular:hover {
                    transform: translateY(-10px) scale(1.05);
                }
                
                .popular-badge {
                    position: absolute;
                    top: -15px;
                    right: 30px;
                    background-color: #1d3618;
                    color: white;
                    font-weight: bold;
                    padding: 8px 15px;
                    border-radius: 30px;
                    font-size: 0.9rem;
                }
                
                .pricing-period {
                    text-align: center;
                    margin-bottom: 20px;
                    background-color: #f0f8f0;
                    padding: 10px;
                    border-radius: 8px;
                }
                
                .pricing-period h3 {
                    font-size: 1.8rem;
                    margin: 0;
                    color: #1d3618;
                }
                
                .pricing-amount {
                    text-align: center;
                    margin-bottom: 10px;
                }
                
                .currency {
                    font-size: 1.2rem;
                    vertical-align: top;
                    color: #333;
                    font-weight: 500;
                    margin-right: 5px;
                }
                
                .amount {
                    font-size: 3.5rem;
                    font-weight: bold;
                    color: #1d3618;
                }
                
                .period {
                    font-size: 1rem;
                    color: #444;
                    margin-left: 5px;
                }
                
                .monthly-equivalent {
                    text-align: center;
                    font-size: 0.9rem;
                    color: #444;
                    height: 25px;
                    margin-bottom: 15px;
                }
                
                .savings-badge {
                    background-color: #f8f4e5;
                    color: #8b7022;
                    font-weight: bold;
                    padding: 8px 15px;
                    border-radius: 30px;
                    text-align: center;
                    width: fit-content;
                    margin: 0 auto 20px auto;
                }
                
                .pricing-features {
                    margin-top: 25px;
                    margin-bottom: 30px;
                }
                
                .pricing-features ul {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }
                
                .pricing-features li {
                    padding: 10px 0;
                    color: #444;
                    font-size: 1rem;
                }
                
                .pricing-features i {
                    color: #1d3618;
                    margin-right: 10px;
                }
                
                .select-plan-button {
                    width: 100%;
                    background-color: white;
                    color: #1d3618;
                    border: 2px solid #1d3618;
                    padding: 12px 0;
                    font-size: 1.1rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                
                .select-plan-button:hover {
                    background-color: rgba(29, 54, 24, 0.1);
                }
                
                .select-plan-button.selected {
                    background-color: #1d3618;
                    color: white;
                }
                
                .checkout-section {
                    text-align: center;
                    margin-top: 30px;
                    background-color: #f8f9fa;
                    padding: 30px;
                    border-radius: 12px;
                }
                
                .checkout-button {
                    background-color: #1d3618;
                    color: white;
                    border: none;
                    padding: 15px 40px;
                    font-size: 1.2rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                
                .checkout-button:hover {
                    background-color: #162a12;
                }
                
                .checkout-button:disabled {
                    background-color: #6c757d;
                    cursor: not-allowed;
                }
                
                .checkout-note {
                    margin-top: 15px;
                    color: #444;
                }
                
                .no-plans-message {
                    text-align: center;
                    padding: 40px;
                    background-color: #f8f9fa;
                    border-radius: 12px;
                    color: #444;
                }
                
                @media (max-width: 960px) {
                    .pricing-boxes {
                        flex-direction: column;
                        align-items: center;
                    }
                    
                    .pricing-box {
                        width: 90%;
                        max-width: 400px;
                    }
                    
                    .pricing-box.popular {
                        transform: none;
                        order: -1;
                    }
                    
                    .pricing-box.popular:hover {
                        transform: translateY(-10px);
                    }
                }
            `}</style>
        </div>
    );
} 